import * as React from 'react';
import { Modal, Button, DatePicker, TimePicker, Upload, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { InfoCircleOutlined, InboxOutlined, CloseOutlined } from '@ant-design/icons';
import withStyles from 'react-jss';
import { useGenericState } from 'src/library/UseGenericState';
import { connect } from 'react-redux';
import { getRequiredInvoiceConfig } from '../Invoices/Invoices.utils';
import { uploadPodFiles } from './DeliveryAcknowledgement.utils';
import moment from 'moment';
import { UploadChangeParam, UploadFile } from 'antd/lib/upload/interface';

const { Dragger } = Upload;

const styles: any = (theme) => ({
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
  },
  infoBox: {
    display: 'flex',
    alignItems: 'flex-start',
    background: '#F0F7FF',
    border: '1px solid #BFDBFE',
    borderRadius: 8,
    padding: '10px 14px',
    marginBottom: 16,
  },
  infoIcon: {
    fontSize: 18,
    color: '#1677ff',
    marginRight: 10,
    marginTop: 2,
  },
  infoText: {
    fontSize: 13,
    color: '#1E3A8A',
    lineHeight: 1.5,
  },
  section: {
    marginBottom: 16,
  },
  sectionLabel: {
    marginBottom: 6,
    fontWeight: 500,
  },
  subtext: {
    color: '#666666',
    fontSize: '10px',
    fontWeight: 400,
  },
  titleContainer: {
    fontSize: 15,
    fontWeight: 600,
  },
  titleSuffix: {
    color: '#0751b8ff',
    marginLeft: 6,
  },
  requiredAsterisk: {
    color: 'red',
    marginLeft: 4,
  },
  timestampContainer: {
    display: 'flex',
    gap: 8,
  },
});

interface DeliveryPickupModalProps {
  classes: any;
  isVisible: boolean;
  actionType: 'delivery' | 'pickup';
  onCancel: () => void;
  onConfirm: (payload: {
    podUrlList: Array<{ url: string; type: string }>;
    eventTime: string | null;
    showPieceLevelActions: boolean;
  }) => Promise<void> | void;
  selectedRows?: Array<string>;
  consignments?: Array<any>;
  consignmentActionConfig?: any;
}

const DeliveryPickupModal = (props: DeliveryPickupModalProps) => {
  const {
    classes,
    isVisible,
    actionType,
    onCancel,
    onConfirm,
    selectedRows = [],
    consignments = [],
    consignmentActionConfig,
  } = props;

  const { t } = useTranslation();

  const [state, setState] = useGenericState({
    fileList: [] as UploadFile[],
    eventDate: null as string | null,
    eventTime: null as string | null,
    isLoading: false,
    isUploading: false,
  });

  const { fileList, eventDate, eventTime, isLoading, isUploading } = state;

  // Get config using isActionHidden logic
  const getConfig = () => {
    if (!consignments?.length || !selectedRows?.length) return {};

    const firstConsignment = consignments.find(
      (cn) => cn.reference_number === selectedRows[0],
    );

    if (!firstConsignment) return {};

    const requiredConfig = getRequiredInvoiceConfig(
      consignmentActionConfig?.flow_config,
      { consignmentData: firstConsignment },
    );

    const statusKey = actionType === 'delivery' ? 'delivered' : 'pickup_completed';
    return {
      config: requiredConfig?.[statusKey] || {},
      showPieceLevelActions: requiredConfig?.show_piece_level_actions || false,
    };
  };

  const { config, showPieceLevelActions } = getConfig();
  const maxPodImages = config?.max_proof_count ?? 1;
  const minPodImages = config?.min_proof_count ?? 1;
  const podRequired = config?.pod_required === true;
  const captureTimestamp = config?.capture_timestamp === true;
  const hideTimestamp = config?.hide_timestamp === true;

  React.useEffect(() => {
    if (!isVisible) {
      setState({
        fileList: [],
        eventDate: null,
        eventTime: null,
        isLoading: false,
        isUploading: false,
      });
    }
  }, [isVisible]);

  const customRequest = (args: any) => {
    const { onSuccess, file } = args;
    setTimeout(() => {
      onSuccess(null, file);
    }, 100);
  };

  const handleUploadChange = async (info: UploadChangeParam) => {
    const files = info.fileList;
    let filesToShow = [];
    if (files?.length) {
      if (maxPodImages > 1) {
        filesToShow = files.slice(-maxPodImages);
      } else {
        filesToShow = files.slice(-1);
      }
    }
    setState({ fileList: filesToShow });
  };

  const handleConfirm = async () => {
    if (podRequired && fileList.length < minPodImages) {
      message.error(t('Please upload POD'));
      return;
    }

    if (!hideTimestamp && captureTimestamp && (!eventDate || !eventTime)) {
      message.error(`${t('Please select')} ${t(actionType === 'delivery' ? 'delivery' : 'pickup')} ${t('timestamp')}`);
      return;
    }

    setState({ isLoading: true, isUploading: true });

    try {
      let podUrlList: Array<{ url: string; type: string }> = [];
      const newPodImages = fileList
        ?.filter((file) => file.originFileObj)
        ?.map((file) => file.originFileObj);

      if (newPodImages?.length) {
        podUrlList = await uploadPodFiles(newPodImages);
      }

      const formattedEventTime = eventDate && eventTime
        ? `${eventDate} ${eventTime}`
        : null;

      await onConfirm({
        podUrlList,
        eventTime: formattedEventTime,
        showPieceLevelActions,
      });
    } catch (error) {
      message.error(t('Failed to upload POD. Please try again.'));
      setState({ isLoading: false, isUploading: false });
    }
  };

  const renderHeader = () => (
    <div className={classes.titleContainer}>
      {actionType === 'delivery' ? t('Mark Delivered') : t('Mark Pickup Completed')}
      {selectedRows?.length === 1 ? (
        <span className={classes.titleSuffix}>- {selectedRows[0]}</span>
      ) : (
        <span className={classes.titleSuffix}> - Bulk Update</span>
      )}
    </div>
  );

  const isButtonDisabled =
    isLoading ||
    isUploading;

  return (
    <Modal
      visible={isVisible}
      title={renderHeader()}
      onCancel={onCancel}
      width={600}
      centered
      footer={
        <div className={classes.modalFooter}>
          <Button onClick={onCancel} disabled={isLoading}>
            {t('Cancel')}
          </Button>
          <Button
            type="primary"
            onClick={handleConfirm}
            disabled={isButtonDisabled}
            loading={isLoading}
          >
            {actionType === 'delivery' ? t('Mark Delivered') : t('Mark Pickup Completed')}
          </Button>
        </div>
      }
    >
      {selectedRows?.length > 1 && (
        <div className={classes.infoBox}>
          <InfoCircleOutlined className={classes.infoIcon} />
          <div className={classes.infoText}>
            {`Same information will be saved against all ${selectedRows?.length} selected consignments.`}
          </div>
        </div>
      )}

      {/* POD Upload Section */}
      <div className={classes.section}>
        <div className={classes.sectionLabel}>
          {actionType === 'delivery' ? t('POD (Proof of Delivery)') : t('POP (Proof of Pickup)')}
          {podRequired && <span className={classes.requiredAsterisk}>*</span>}
        </div>
        <Dragger
          fileList={fileList}
          accept=".png, .jpeg, .pdf, .jpg"
          name="file"
          multiple={maxPodImages > 1}
          customRequest={customRequest}
          onChange={handleUploadChange}
          showUploadList={{
            showPreviewIcon: true,
            removeIcon: <CloseOutlined />,
          }}
          style={{ padding: '16px' }}
        >
          <p style={{ fontSize: '12px' }} />
          <p className="ant-upload-drag-icon">
            <InboxOutlined style={{ color: '#CCCCCC' }} />
          </p>
          <p
            style={{
              color: '#525252',
              fontSize: '14px',
              fontWeight: 400,
            }}
          >
            <b>
              {t('Click or drag file to this area to upload')}
            </b>
            <div className={classes.subtext}>
              {maxPodImages > 1 ? `${t('maximum')} ${maxPodImages} ${t('allowed')}` : t('Support for a single upload')}
            </div>
          </p>
        </Dragger>
      </div>

      {/* Timestamp Section */}
      {!hideTimestamp && (
        <div className={classes.section}>
          <div className={classes.sectionLabel}>
            {actionType === 'delivery' ? t('Delivery Time Stamp') : t('Pickup Time Stamp')}
            {captureTimestamp && <span className={classes.requiredAsterisk}>*</span>}
          </div>
          <div className={classes.timestampContainer}>
            <DatePicker
              format="YYYY-MM-DD"
              onChange={(date, dateString) => setState({ eventDate: dateString })}
              placeholder={t('Select date')}
              disabled={isLoading}
              disabledDate={(current) => current.isAfter(moment())}
              value={eventDate ? moment(eventDate) : null}
              style={{ flex: 1 }}
            />
            <TimePicker
              format="HH:mm"
              onChange={(time, timeString) => setState({ eventTime: timeString })}
              placeholder={t('Select time')}
              disabled={isLoading}
              value={eventTime ? moment(eventTime, 'HH:mm') : null}
              style={{ flex: 1 }}
            />
          </div>
        </div>
      )}
    </Modal>
  );
};

const mapStateToProps = ({ masterData }) => {
  return {
    consignmentActionConfig: masterData?.vendor_config?.consignment_action_config || {},
  };
};

const DeliveryPickupModalStyled = withStyles(styles, { injectTheme: true })(DeliveryPickupModal);
export default connect(mapStateToProps, null)(DeliveryPickupModalStyled);
